"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import { BulletList } from "@/app/(site)/_components/Molecules/BulletList"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { RelatedVariant } from "@/lib/content-service/types/ContentServiceProduct"
import { t } from "@/translations"

type ProductCardDetailsProps = {
    displayedSwatches: RelatedVariant[]
    extraColorsCount: number
    langCode: string
    handleChangeVariant: (product?: RelatedVariant) => void
    uri?: string
    name: string
    price?: string
    priceBeforeDiscount: string
    showAsOnSale: boolean
    productBenefits?: string[]
    available: boolean
    showBenefits?: boolean
}

export default function ProductCardDetails({
    displayedSwatches,
    extraColorsCount,
    langCode,
    handleChangeVariant,
    uri,
    name,
    price,
    priceBeforeDiscount,
    showAsOnSale,
    productBenefits,
    available,
    showBenefits
}: ProductCardDetailsProps) {
    return (
        <div className="grid grid-cols-2 overflow-hidden">
            <div className="col-span-2 md:col-span-1">
                <DynamicLink href={`/product/${uri}`} prefetch={false}>
                    <Text size="small" weight="regular" className="md:text-16">
                        {name}
                    </Text>
                    <Text
                        size="small"
                        weight="regular"
                        className="mb-8 flex gap-8 text-main-dark-grey md:text-16"
                    >
                        <span>{price}</span>
                        {showAsOnSale && (
                            <span className="line-through">{priceBeforeDiscount}</span>
                        )}
                    </Text>
                </DynamicLink>
                <div className="group mt-14 flex h-24 w-full px-2">
                    <div
                        className={`hidden w-full items-center gap-8 md:group-hover:flex ${
                            showBenefits ? "flex-wrap" : ""
                        }`}
                    >
                        {displayedSwatches.map((swatch, index) => {
                            return (
                                <DynamicLink
                                    href={`/product/${swatch.uri}`}
                                    className="w-24 cursor-pointer"
                                    onMouseEnter={() => handleChangeVariant(swatch)}
                                    key={`${index}-${swatch?.name ?? swatch.color_swatch?.hex ?? ""}`}
                                >
                                    <div className="relative h-24 w-24 rotate-[-50deg]">
                                        {swatch.lens?.image ? (
                                            <BaseImage
                                                unoptimized
                                                alt={swatch.name}
                                                src={swatch.lens.image.url}
                                                width={100}
                                                height={100}
                                                className="absolute inset-0 z-20 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                    backgroundColor:
                                                        swatch.lens?.hex ??
                                                        swatch.color_swatch?.hex
                                                }}
                                            />
                                        )}
                                        <div
                                            className="absolute inset-0 rounded-full"
                                            style={{
                                                clipPath:
                                                    "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                backgroundColor: swatch.color_swatch?.hex
                                            }}
                                        />
                                    </div>
                                </DynamicLink>
                            )
                        })}

                        {extraColorsCount > 0 && (
                            <Text
                                size="small"
                                weight="regular"
                                className="text-main-dark-grey md:text-16"
                            >
                                +{extraColorsCount} {t(langCode, "more")}
                            </Text>
                        )}
                    </div>

                    {displayedSwatches.length > 1 && (
                        <Text
                            size="small"
                            weight="regular"
                            className="text-main-dark-grey md:text-16 md:group-hover:hidden"
                        >
                            {displayedSwatches.length} {t(langCode, "Colors")}
                        </Text>
                    )}
                </div>
            </div>
            <div className="col-span-2 mt-8 md:col-span-1">
                {available ? (
                    Array.isArray(productBenefits) &&
                    productBenefits.length > 0 &&
                    showBenefits && <BulletList title={productBenefits.slice(0, 3)} />
                ) : (
                    <Text
                        size="medium"
                        weight="regular"
                        className="!text-14 text-black md:!text-16"
                    >
                        {t(langCode, "OUT OF STOCK")}
                    </Text>
                )}
            </div>
        </div>
    )
}
